import { ShiftManagementMainPageDocument } from 'generated/resources/graphql'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'

import { SHIFT_CONFLICTS } from 'modules/shift_management/hooks/reducers/filter'
import { MAX_ITEMS_PER_PAGE } from 'modules/shift_management/hooks/reducers/pagination'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { useOrderOptions } from 'modules/shift_management/hooks/useOrderEmployees'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import { TShiftsRow } from 'modules/shift_management/store/internal/models/EmployeeWithShifts'
import ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'
import { GROUP_TYPES } from 'modules/shift_management/store/internal/models/View'

const useGetShiftsRows = () => {
  const [filters] = useFilters()
  const [{ page }] = usePagination()
  const [{ view }] = useSchedule()
  const { startAt, endAt } = view
  const { localStorageOrderEmployeesBy, localStorageOrderEmployeesDir } = useOrderOptions()
  const currentPage = filters.status_id === SHIFT_CONFLICTS ? null : page
  const [shiftRowsMemo] = useShiftsRows()
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureFlag(
    ShiftManagementFeatureFlags.DEV_SHIFT_MANAGEMENT_AUTOMATION
  )

  // TODO:
  // - Define targetView as hook param.
  // - Define work areas view query
  const { groupType } = view
  const document =
    groupType === GROUP_TYPES.employees
      ? ShiftManagementMainPageDocument
      : ShiftManagementMainPageDocument // Work Areas query

  const { loading, data, refetch } = useQuery(document, {
    // TODO: Remove this once we migrate to GraphQL mutations properly updating cache
    // and remove the feature flag "dev_shift_management_graphql_migration"
    fetchPolicy: 'network-only',
    variables: {
      // TODO: MAX_ITEMS_PER_PAGE
      // - status_id
      // features,
      contractOnDate: startAt,
      orderBy: localStorageOrderEmployeesBy,
      orderDir: localStorageOrderEmployeesDir,
      page: currentPage,
      perPage: MAX_ITEMS_PER_PAGE,
      customFilters: filters.custom_filters,
      // - Accept employeeIds to refresh some isolated rows on update/delete
      employeeIds: filters.employee_ids?.map(Number) || null,
      endAt,
      logicOperator: filters.logic_operator,
      shiftIds: filters.shift_ids,
      startAt,
      terminated: filters.terminated,
    },
  })

  const shiftsRows = useUnsafeMemo(() => {
    const result = data?.shiftManagement?.employeesPermissionsConnection?.nodes || []
    return result.map((shiftsRow: TShiftsRow) => ShiftsRow.buildFromGraphQL(shiftsRow))
  }, [data?.shiftManagement.employeesPermissionsConnection.nodes])

  useUnsafeEffect(() => {
    // we need to add this refetch under the FF check because we are not using graphQL for the mutations yet.
    // once we start using graphQL mutations for removing and creating shifts this hook might be removed
    if (!isAutomationEnabled) return
    refetch()
  }, [shiftRowsMemo])

  return { shiftsRows, loading, refetch }
}

export default useGetShiftsRows
